import React from 'react';
import { ClerkProvider } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { srRS, deDE, enUS } from '@clerk/localizations';

const PUBLISHABLE_KEY = process.env.VITE_CLERK_PUBLISHABLE_KEY;
const VITE_CLERK_SIGN_IN_FORCE_REDIRECT_URL = process.env.VITE_CLERK_SIGN_IN_FORCE_REDIRECT_URL;
const VITE_CLERK_SIGN_UP_FORCE_REDIRECT_URL = process.env.VITE_CLERK_SIGN_UP_FORCE_REDIRECT_URL;

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key");
}

interface ClerkProviderWithI18nProps {
  children: React.ReactNode;
}

const ClerkProviderWithI18n: React.FC<ClerkProviderWithI18nProps> = ({ children }) => {
  const { t, i18n } = useTranslation();

  const getClerkLocalization = () => {
    const lang = i18n.language;
    if (lang.startsWith('sr')) {
      return srRS;
    }
    else if (lang.startsWith('de')) {
      return deDE;
    }
    else if (lang.startsWith('mk')) {
      return enUS;
    }
    else if (lang.startsWith('slo')) {
      return enUS;
    }
    else if (lang.startsWith('en')) {
      return enUS;
    }
    return enUS; // Defaults to English
  };

  return (
    <ClerkProvider
      localization={getClerkLocalization()}
      publishableKey={PUBLISHABLE_KEY}
      signInForceRedirectUrl={VITE_CLERK_SIGN_IN_FORCE_REDIRECT_URL}
      signUpForceRedirectUrl={VITE_CLERK_SIGN_UP_FORCE_REDIRECT_URL}
      appearance={{
        variables: {
          colorPrimary: '#0284c7', // sky-600
          colorText: '#334155', // slate-700
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
          borderRadius: '0.5rem', // rounded-lg
        },
        elements: {
          rootBox: 'w-full flex items-center justify-center',
          modalBackdrop: 'flex items-center justify-center p-0 z-[9999]',
          modalContent: 'w-full h-full flex items-center justify-center',
          modalCloseButton: 'text-slate-600 hover:text-slate-900 focus:shadow-none transition-colors border-none',
          card: 'shadow-xl rounded-lg border-none',
          cardBox: 'w-full sm:max-w-md shadow-none border-none mx-auto',
          userProfileCard: 'w-full h-full sm:max-w-none sm:rounded-none shadow-none border-none',
          accountProfileCard: 'w-full h-full sm:max-w-none sm:rounded-none shadow-none border-none',
          userProfilePage: 'p-4 sm:p-10',
          accountProfilePage: 'p-4 sm:p-10',
          userButtonPopoverCard: 'shadow-xl border border-slate-200',
        },
        layout: {
          unsafe_disableDevelopmentModeWarnings: true,
          socialButtonsPlacement: 'bottom',
          termsPageUrl: t('helperMenu.termsOfUseLink'),
          privacyPageUrl: t('helperMenu.privacyPolicyLink'),
        },
      }}
    >{children}</ClerkProvider>
  );
};

export default ClerkProviderWithI18n;